
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';
import { pinEmailSchema, PinEmailTransferFormProps } from './types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { processTransfer } from './paymentProcessor';
import { checkExistingPin } from './pinUtils';

type TransferFormValues = z.infer<typeof pinEmailSchema>;

const PinEmailTransferForm: React.FC<PinEmailTransferFormProps> = ({ 
  onTransferComplete,
  onBack
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isPinExists, setIsPinExists] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string>('stripe');
  
  const { email, amount } = location.state || {};
  
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<TransferFormValues>({
    resolver: zodResolver(pinEmailSchema),
    defaultValues: {
      email: email || '',
      amount: amount || 0,
      pin: ''
    }
  });

  useEffect(() => {
    // Check if the payment method is included in the location state
    if (location.state?.paymentMethod) {
      setPaymentMethod(location.state.paymentMethod);
    }
    
    // Check if PIN exists for the email
    const checkPin = async () => {
      if (email) {
        const result = await checkExistingPin(email);
        setIsPinExists(result.exists);
      }
    };
    
    checkPin();
  }, [email, location.state]);
  
  const onSubmit = async (data: TransferFormValues) => {
    setIsLoading(true);
    
    try {
      // Process the transfer with the specified payment method
      const success = await processTransfer(
        data.email, 
        data.pin, 
        data.amount,
        isPinExists,
        paymentMethod
      );
      
      if (success) {
        toast.success('Transfer successful', { 
          description: paymentMethod === 'bitcoin' 
            ? 'Bitcoin transaction has been initiated' 
            : 'Your payment has been processed successfully'
        });
        onTransferComplete();
      } else {
        toast.error('Transfer failed', {
          description: 'Please check your PIN and try again'
        });
      }
    } catch (error) {
      console.error('Transfer error:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="flex flex-col items-start">
        <Button 
          type="button" 
          variant="ghost" 
          size="sm"
          className="mb-4"
          onClick={onBack}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <div className="w-full space-y-4">
          <div className="bg-blue-50 p-3 rounded-md mb-4">
            <h3 className="text-sm font-medium text-blue-800">
              {paymentMethod === 'bitcoin' ? 'Bitcoin Transfer' : 'Secure Email Transfer'}
            </h3>
            <p className="text-xs text-blue-600 mt-1">
              {paymentMethod === 'bitcoin' 
                ? 'Send Bitcoin to the specified address after verification' 
                : 'Enter your PIN to complete the secure transfer'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">
              {paymentMethod === 'bitcoin' ? 'Bitcoin Address' : 'Email Address'}
            </Label>
            <Input 
              id="email"
              {...register('email')}
              className="w-full"
              placeholder={paymentMethod === 'bitcoin' ? 'Enter Bitcoin address' : 'Enter email'}
              disabled={!!email}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Amount (USD)</Label>
            <Input 
              id="amount"
              type="number"
              step="0.01"
              {...register('amount', { valueAsNumber: true })}
              className="w-full"
              placeholder="Enter amount"
              disabled={!!amount}
            />
            {errors.amount && (
              <p className="text-sm text-red-500">{errors.amount.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="pin">Security PIN</Label>
              {isPinExists && (
                <span className="text-xs text-green-600">PIN exists</span>
              )}
            </div>
            <Input 
              id="pin"
              {...register('pin')}
              className="w-full"
              placeholder={isPinExists ? 'Enter your PIN' : 'Create a new PIN'}
              type="password"
            />
            {errors.pin && (
              <p className="text-sm text-red-500">{errors.pin.message}</p>
            )}
          </div>

          <Button 
            type="submit" 
            className="w-full mt-6"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                {paymentMethod === 'bitcoin' 
                  ? 'Complete Bitcoin Transfer' 
                  : 'Complete Secure Transfer'}
              </>
            )}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default PinEmailTransferForm;
